import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { payloadPreviewText } from './runCells';
import TrodoLoader from 'shared/components/ui/TrodoLoader';

/**
 * Semantic cluster map: each cluster is a centroid hub with a halo of
 * conversation dots around it. No lines connecting the two — the
 * spatial proximity (dot near a hub) carries the membership signal
 * cleanly enough.
 *
 * Visual contract:
 *   - Each cluster gets ONE colour from a deterministic palette (hashed
 *     by cluster_id), reused for both the hub and its member dots.
 *   - Conversation dots are SOLID-FILLED when satisfaction ≥ 0.5 ("happy")
 *     and HOLLOW (ring only, white interior) when satisfaction < 0.5
 *     ("unhappy"). For issue maps, the same scheme uses rage inverted:
 *     low rage = solid, high rage = hollow.
 *   - Hub size scales with run_count so the eye reads dominant intents.
 *
 * Props:
 *   clusters         [{ id, label, run_count, conversation_count,
 *                       satisfaction_score, rage_score, agent_name, x, y }]
 *   conversations    [{ id, cluster_id, x, y, rage_score, satisfaction_score,
 *                       status, input_preview, run_id, conversation_id }]
 *   kind             'use_case' (kept for back-compat; 'issue' removed in
 *                                Phase 3 — use the IssuesPage v2 surface)
 *   onSelect(cluster) — hub clicked (open cluster detail)
 *   onSelectConversation(conv) — dot clicked (open AgentRunDetailPage)
 *
 * Back-compat: legacy callers that passed `points` get transparently
 * remapped to `clusters` + empty `conversations`.
 */
export default function ClusterMap({
  clusters,
  conversations = [],
  points, // legacy alias for `clusters`
  loading,
  onSelect,
  onSelectConversation,
  kind = 'use_case',
  minViewportHeight = '70vh',
}) {
  // Memoised so the layout below is not recomputed on every hover or pan.
  const hubs = useMemo(() => clusters || points || [], [clusters, points]);
  const convs = useMemo(() => conversations || [], [conversations]);

  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const gridId = useId();
  const minorGridId = useId();
  const [size, setSize] = useState({ w: 900, h: 520 });
  const [view, setView] = useState({ k: 1, tx: 0, ty: 0 });
  const [hoverHub, setHoverHub] = useState(null);
  const [hoverConv, setHoverConv] = useState(null);
  const dragRef = useRef(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        setSize({ w: Math.max(320, width), h: Math.max(260, height) });
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Plan B3: keyboard navigation. +/- zoom, 0 reset, arrows pan. We attach
  // to the container (not window) so the map only hijacks keys when the
  // user has explicitly focused it — otherwise typing in a filter input
  // would zoom the map.
  const handleKeyDown = (e) => {
    switch (e.key) {
      case '+': case '=':
        setView((v) => ({ ...v, k: Math.min(8, v.k * 1.2) }));
        e.preventDefault();
        break;
      case '-': case '_':
        setView((v) => ({ ...v, k: Math.max(0.5, v.k / 1.2) }));
        e.preventDefault();
        break;
      case '0':
        setView({ k: 1, tx: 0, ty: 0 });
        e.preventDefault();
        break;
      case 'ArrowUp':
        setView((v) => ({ ...v, ty: v.ty + 20 }));
        e.preventDefault();
        break;
      case 'ArrowDown':
        setView((v) => ({ ...v, ty: v.ty - 20 }));
        e.preventDefault();
        break;
      case 'ArrowLeft':
        setView((v) => ({ ...v, tx: v.tx + 20 }));
        e.preventDefault();
        break;
      case 'ArrowRight':
        setView((v) => ({ ...v, tx: v.tx - 20 }));
        e.preventDefault();
        break;
      default:
        break;
    }
  };

  // Compute shared bounds over hubs AND conversations so both sets use
  // the same coord transform. The backend now writes deterministic
  // shared-frame coords at clustering time (layoutCoords.recomputeLayoutForPair)
  // — anchors via classical MDS over medoids, members placed analytically
  // around their anchor — so we trust the stored x/y and just normalise.
  const { hubsMapped, convsMapped, hubById, extents } = useMemo(() => {
    const all = [...hubs, ...convs].filter((p) => Number.isFinite(p.x) && Number.isFinite(p.y));
    if (all.length === 0) {
      return {
        hubsMapped: [],
        convsMapped: [],
        hubById: new Map(),
        extents: null,
      };
    }
    let minX = Infinity; let maxX = -Infinity; let minY = Infinity; let maxY = -Infinity;
    for (const p of all) {
      if (p.x < minX) minX = p.x;
      if (p.x > maxX) maxX = p.x;
      if (p.y < minY) minY = p.y;
      if (p.y > maxY) maxY = p.y;
    }
    const spanX = (maxX - minX) || 1;
    const spanY = (maxY - minY) || 1;
    const sizeRef = hubs.reduce(
      (m, p) => Math.max(m, p.conversation_count || p.run_count || 0),
      1,
    );
    const mappedHubs = hubs
      .filter((p) => Number.isFinite(p.x) && Number.isFinite(p.y))
      .map((p) => {
        const size = p.conversation_count || p.run_count || 0;
        // Each cluster gets its own color, regardless of topic — same-topic
        // clusters share spatial proximity (member dots overlap) which
        // already conveys the grouping; recoloring loses the per-cluster
        // identity signal that lets users spot which sub-flow is which.
        return {
          ...p,
          _nx: (p.x - minX) / spanX,
          _ny: (p.y - minY) / spanY,
          // Smaller hub footprint than before so the member dots are the
          // primary visual element. Hub still scales with conversation
          // count so dominant intents read at a glance.
          _r: Math.max(8, Math.min(22, 8 + 14 * Math.sqrt(size / sizeRef))),
          _color: clusterColor(`c${p.id}`),
        };
      });
    const idx = new Map(mappedHubs.map((h) => [h.id, h]));
    const mappedConvs = convs
      .filter((p) => Number.isFinite(p.x) && Number.isFinite(p.y))
      .map((p, i) => {
        const parent = idx.get(p.cluster_id);
        const isNoise = !parent;
        const color = parent ? parent._color : 'var(--text-muted)';
        const filled = isHappy(p);
        return {
          ...p,
          // A run can belong to more than one cluster, so it appears here more
          // than once and `id` is not unique — React was warning about
          // duplicate keys and is free to drop or duplicate those dots.
          // cluster_id + index disambiguates without relying on id alone.
          _key: `conv-${p.cluster_id ?? 'noise'}-${p.id}-${i}`,
          _nx: (p.x - minX) / spanX,
          _ny: (p.y - minY) / spanY,
          _color: color,
          _filled: filled,
          _isNoise: isNoise,
        };
      });
    return {
      hubsMapped: mappedHubs,
      convsMapped: mappedConvs,
      hubById: idx,
      extents: {
        minX,
        maxX,
        minY,
        maxY,
        spanX,
        spanY,
      },
    };
  }, [hubs, convs]);

  const padding = 56;
  const gridSize = 72;
  // Room at the bottom for the legend, so no cluster sits under it.
  const legendRoom = 28;
  const viewW = Math.max(1, size.w - padding * 2);
  const viewH = Math.max(1, size.h - padding * 2 - legendRoom);

  // Wheel zoom is attached as a NON-passive listener in the effect below.
  // React's synthetic onWheel is passive, so preventDefault() there is a no-op
  // that floods the console with warnings on every scroll tick.
  const onMouseDown = (e) => {
    dragRef.current = { x: e.clientX, y: e.clientY, tx: view.tx, ty: view.ty };
  };
  const onMouseMove = (e) => {
    if (!dragRef.current) return;
    const dx = e.clientX - dragRef.current.x;
    const dy = e.clientY - dragRef.current.y;
    setView((v) => ({ ...v, tx: dragRef.current.tx + dx, ty: dragRef.current.ty + dy }));
  };
  const onMouseUp = () => { dragRef.current = null; };

  // One scale for both axes (no stretching), centred: the clusters fill the
  // view at their true proportions, which is what "fit" means.
  const fitScale = extents ? Math.min(viewW / extents.spanX, viewH / extents.spanY) : 1;
  const fitOffX = extents ? (viewW - extents.spanX * fitScale) / 2 : 0;
  const fitOffY = extents ? (viewH - extents.spanY * fitScale) / 2 : 0;
  const coordFor = (p) => (extents
    ? [(p.x - extents.minX) * fitScale + fitOffX, (extents.maxY - p.y) * fitScale + fitOffY]
    : [p._nx * viewW, (1 - p._ny) * viewH]);

  // Non-passive wheel listener so zoom can preventDefault (no page scroll)
  // without the passive-listener console warning that floods on every tick.
  useEffect(() => {
    const el = svgRef.current;
    if (!el) return undefined;
    const handler = (e) => {
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      const pointerX = e.clientX - rect.left;
      const pointerY = e.clientY - rect.top;
      const zoomFactor = Math.exp(-e.deltaY * 0.0012);
      setView((v) => {
        const nextK = Math.max(0.5, Math.min(8, v.k * zoomFactor));
        const kRatio = nextK / v.k;
        return { k: nextK, tx: pointerX - (pointerX - v.tx) * kRatio, ty: pointerY - (pointerY - v.ty) * kRatio };
      });
    };
    el.addEventListener('wheel', handler, { passive: false });
    return () => el.removeEventListener('wheel', handler);
  }, []);

  // Hover highlighting: when a hub is hovered, fade everything not attached
  // to it. When a conversation is hovered, dim everything except that dot
  // and its hub.
  const dimConv = (c) => {
    if (hoverHub != null) return c.cluster_id !== hoverHub;
    if (hoverConv != null) return c._key !== hoverConv;
    return false;
  };

  const dimHub = (h) => {
    if (hoverConv != null) {
      const c = convsMapped.find((x) => x._key === hoverConv);
      return !c || c.cluster_id !== h.id;
    }
    if (hoverHub != null) return h.id !== hoverHub;
    return false;
  };

  const emptyState = !loading && hubs.length === 0;

  const zoomAroundViewportCenter = (scaleMultiplier) => {
    const cx = size.w / 2;
    const cy = size.h / 2;
    setView((v) => {
      const nextK = Math.max(0.5, Math.min(8, v.k * scaleMultiplier));
      const kRatio = nextK / v.k;
      return {
        k: nextK,
        tx: cx - (cx - v.tx) * kRatio,
        ty: cy - (cy - v.ty) * kRatio,
      };
    });
  };

  const zoomIn = () => zoomAroundViewportCenter(1.25);
  const zoomOut = () => zoomAroundViewportCenter(1 / 1.25);
  const zoomReset = () => setView({ k: 1, tx: 0, ty: 0 });
  const zoomFit = () => setView({ k: 1, tx: 0, ty: 0 });
  // Double-click a hub → centre + zoom (plan B3). Event delegation on the
  // SVG would be cleaner but the per-circle onClick already stops
  // propagation so we wire it per-hub via onDoubleClick prop below.
  const focusOnHub = (hub) => {
    const [cx, cy] = coordFor(hub);
    const k = 2.5;
    setView({
      k,
      tx: size.w / 2 - ((cx + padding) * k),
      ty: size.h / 2 - ((cy + padding) * k),
    });
  };

  // Which hub names fit at this zoom without overlapping (screen space).
  const labelled = new Set();
  {
    const placed = [];
    const byRuns = [...hubsMapped].sort((a, b) => (b.run_count || 0) - (a.run_count || 0));
    for (const h of byRuns) {
      const [x, y] = coordFor(h);
      const sx = (x + padding) * view.k + view.tx;
      const sy = (y + padding) * view.k + view.ty - (h._r * view.k + 10);
      const w = Math.min(48, (h.label || '').length) * 6.4 + 8;
      const box = { l: sx - w / 2, r: sx + w / 2, t: sy - 12, b: sy + 2 };
      if (!placed.some((o) => box.l < o.r && box.r > o.l && box.t < o.b && box.b > o.t)) {
        placed.push(box);
        labelled.add(h.id);
      }
    }
  }

  return (
    <div
      ref={containerRef}
      className="relative h-full min-h-[320px] w-full overflow-hidden rounded-xl border border-[color:var(--border-default)] bg-surface focus:outline-none"
      style={{ fontFamily: 'Inter, sans-serif', minHeight: minViewportHeight }}
      tabIndex={0}
      onKeyDown={handleKeyDown}
    >
      {loading ? (
        <div className="absolute inset-0 z-10 flex items-center justify-center">
          <TrodoLoader size={64} label="Loading the map" />
        </div>
      ) : null}
      {emptyState ? (
        <div className="absolute inset-0 flex items-center justify-center text-[12px] text-[color:var(--text-muted)]">
          No use-case clusters with centroids to map yet.
        </div>
      ) : null}

      <svg
        ref={svgRef}
        className="absolute inset-0"
        preserveAspectRatio="none"
        width="100%"
        height="100%"
        viewBox={`0 0 ${size.w} ${size.h}`}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseUp}
        style={{ cursor: dragRef.current ? 'grabbing' : 'grab' }}
      >
        <defs>
          {/* Infinite world grid: this pattern repeats over a huge world rect.
              The grid lives inside the transformed world layer, so it pans and
              zooms naturally like Figma/Miro rather than clipping to viewport. */}
          <pattern id={gridId} width={gridSize} height={gridSize} patternUnits="userSpaceOnUse">
            <path d={`M ${gridSize} 0 L 0 0 0 ${gridSize}`} fill="none" stroke="var(--border-default)" strokeWidth="1" />
          </pattern>
          <pattern
            id={minorGridId}
            width={gridSize / 4}
            height={gridSize / 4}
            patternUnits="userSpaceOnUse"
          >
            <path
              d={`M ${gridSize / 4} 0 L 0 0 0 ${gridSize / 4}`}
              fill="none"
              stroke="var(--surface-hover)"
              strokeWidth="0.6"
            />
          </pattern>
        </defs>

        {/* Fixed viewport paint: uses the sunken surface token so the map
            background flips with light/dark theme. */}
        <rect x={0} y={0} width={size.w} height={size.h} fill="var(--surface-base)" />

        {/* Grid covers full viewport edge-to-edge at all times. We apply pan/zoom
            to the pattern itself (not the rect), so the visible canvas remains
            fully covered while the grid moves like an infinite board. */}
        <defs>
          <pattern
            id={`${minorGridId}-world`}
            href={`#${minorGridId}`}
            patternTransform={`translate(${view.tx}, ${view.ty}) scale(${view.k})`}
          />
          <pattern
            id={`${gridId}-world`}
            href={`#${gridId}`}
            patternTransform={`translate(${view.tx}, ${view.ty}) scale(${view.k})`}
          />
        </defs>

        <rect
          x={0}
          y={0}
          width={size.w}
          height={size.h}
          fill={`url(#${gridId}-world)`}
          style={{ pointerEvents: 'none' }}
        />

        {/* Transformed world. Only this layer pans/zooms. */}
        <g transform={`translate(${view.tx}, ${view.ty}) scale(${view.k})`}>
          <g transform={`translate(${padding}, ${padding})`}>

          {/* Cluster hubs: drawn FIRST so member dots stack on top.
              Lighter fill (background tint) so the dots are the primary
              visual element. The hub remains clickable for cluster
              detail; labels render above at moderate zoom. */}
          {hubsMapped.map((p) => {
            const [cx, cy] = coordFor(p);
            const isHover = hoverHub === p.id;
            const showLabel = isHover || labelled.has(p.id);
            const dim = dimHub(p);
            return (
              <g key={`hub-${p.id}`} transform={`translate(${cx}, ${cy})`}>
                <circle
                  r={p._r}
                  fill={p._color}
                  fillOpacity={dim ? 0.08 : (isHover ? 0.32 : 0.22)}
                  stroke={isHover ? 'var(--text-primary)' : p._color}
                  strokeWidth={isHover ? 1.6 : 0.8}
                  strokeOpacity={dim ? 0.2 : (isHover ? 1 : 0.55)}
                  onMouseEnter={() => setHoverHub(p.id)}
                  onMouseLeave={() => setHoverHub((h) => (h === p.id ? null : h))}
                  onClick={(e) => { e.stopPropagation(); onSelect?.(p); }}
                  onDoubleClick={(e) => { e.stopPropagation(); focusOnHub(p); }}
                  style={{ cursor: 'pointer' }}
                />
                {/* Names where they fit: the biggest clusters first, any label that
                    would overlap one already placed waits for a closer zoom. */}
                {showLabel && (
                  <text
                    y={-p._r - 6}
                    textAnchor="middle"
                    fontSize={(isHover ? 12 : 11) / Math.max(1, view.k * 0.85)}
                    fontWeight={isHover ? 600 : 500}
                    fill={dim ? 'var(--text-muted)' : 'var(--text-secondary)'}
                    pointerEvents="none"
                  >
                    {(p.label || '(unlabeled)').slice(0, 48)}
                  </text>
                )}
              </g>
            );
          })}

          {/* Conversation dots: drawn AFTER hubs so they sit on top.
              One colour per cluster (matches the hub). Filled circle
              when the conversation is "happy" (satisfaction ≥ 0.5),
              hollow ring when "unhappy" (< 0.5). No connecting lines —
              spatial proximity to the hub conveys membership. */}
          {convsMapped.map((c) => {
            const [cx, cy] = coordFor(c);
            const dim = dimConv(c);
            // Hover keys on _key, not id: with a run in two clusters, keying
            // on id lit up both of its dots at once.
            const hovered = hoverConv === c._key;
            const r = hovered ? 4.2 : 3.4;
            return (
              <circle
                key={c._key}
                cx={cx}
                cy={cy}
                r={r}
                fill={c._filled ? c._color : 'var(--surface-base)'}
                fillOpacity={dim ? 0.2 : 1}
                stroke={hovered ? 'var(--text-primary)' : c._color}
                strokeWidth={hovered ? 1.6 : 1.4}
                strokeOpacity={dim ? 0.2 : 1}
                onMouseEnter={() => setHoverConv(c._key)}
                onMouseLeave={() => setHoverConv((h) => (h === c._key ? null : h))}
                onClick={(e) => { e.stopPropagation(); onSelectConversation?.(c); }}
                style={{ cursor: onSelectConversation ? 'pointer' : 'default' }}
              />
            );
          })}
          </g>
        </g>
      </svg>

      {/* Zoom controls (plan B3). Lives top-right so it doesn't collide
          with the hover card. Keyboard shortcuts (+/- 0 arrows) replicate
          these actions when the container has focus. */}
      <div className="absolute right-3 top-3 flex flex-col overflow-hidden rounded-lg border border-[color:var(--border-default)] bg-surface text-[12px] text-[color:var(--text-secondary)]">
        <button
          type="button"
          onClick={zoomIn}
          title="Zoom in (+)"
          className="h-7 w-7 border-b border-[color:var(--border-default)] hover:bg-[color:var(--surface-hover)]"
        >+</button>
        <button
          type="button"
          onClick={zoomOut}
          title="Zoom out (-)"
          className="h-7 w-7 border-b border-[color:var(--border-default)] hover:bg-[color:var(--surface-hover)]"
        >−</button>
        <button
          type="button"
          onClick={zoomFit}
          title="Fit to view"
          className="h-7 w-7 border-b border-[color:var(--border-default)] hover:bg-[color:var(--surface-hover)] text-[11px]"
        >⤢</button>
        <button
          type="button"
          onClick={zoomReset}
          title="Reset (0)"
          className="h-7 w-7 hover:bg-[color:var(--surface-hover)] text-[11px]"
        >0</button>
      </div>

      {/* Legend. Two axes:
            - colour encodes which CLUSTER a dot belongs to (matches hub).
            - filled vs hollow encodes sentiment (happy vs unhappy). */}
      <div className="pointer-events-none absolute bottom-3 left-3 flex items-center gap-3 text-[11px] text-[color:var(--text-muted)]">
        <span>Size = runs</span>
        <div className="flex items-center gap-3">
          <LegendSwatch filled label="happy" />
          <LegendSwatch filled={false} label="unhappy" />
        </div>
      </div>

      {hoverHub != null ? (
        <HoverCard cluster={hubsMapped.find((p) => p.id === hoverHub)} />
      ) : null}
      {hoverConv != null && hoverHub == null ? (
        <ConversationHoverCard conv={convsMapped.find((c) => c._key === hoverConv)} />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Colour mapping. ONE colour per cluster, hashed deterministically by
// cluster_id from a curated 20-colour palette. Same colour reused for the
// hub and its member dots so the eye reads cluster membership at a glance.
// Sentiment encoding moved to filled-vs-hollow so the colour axis is solely
// "which cluster".
// ---------------------------------------------------------------------------

// Warm, quiet tones from the brand (ember, sand, stone, clay) rather than a
// rainbow: clusters stay tellable apart without the map shouting.
const CLUSTER_PALETTE = [
  '#e07a45', // ember
  '#a39e97', // stone
  '#c9a27e', // sand
  '#b4541f', // clay
  '#7d8b8f', // slate
  '#d9b99b', // dune
  '#8f6a55', // umber
  '#c6c0b8', // mist
  '#f0a77a', // apricot
  '#6f6a64', // basalt
];

function clusterColor(clusterId) {
  // Cheap deterministic hash so reruns of the page show the same palette
  // for the same cluster ids.
  const s = String(clusterId || '');
  let h = 0;
  for (let i = 0; i < s.length; i += 1) {
    h = (h * 31 + s.charCodeAt(i)) | 0;
  }
  return CLUSTER_PALETTE[Math.abs(h) % CLUSTER_PALETTE.length];
}

/**
 * "Happy" if satisfaction ≥ 0.5. Conversations with no sentiment signal
 * default to happy/filled (the common case is "fine, not flagged"; a
 * hollow dot would falsely suggest a problem).
 */
function isHappy(conv) {
  if (!Number.isFinite(conv.satisfaction_score)) return true;
  return Number(conv.satisfaction_score) >= 0.5;
}

function LegendSwatch({ filled, label }) {
  return (
    <span className="inline-flex items-center gap-1">
      <span
        className="inline-block h-2.5 w-2.5 rounded-full"
        style={{
          background: filled ? 'var(--text-secondary)' : 'var(--surface-base)',
          border: '1.5px solid var(--text-secondary)',
        }}
      />
      <span>{label}</span>
    </span>
  );
}

function HoverCard({ cluster }) {
  if (!cluster) return null;
  return (
    <div className="pointer-events-none absolute right-3 top-3 max-w-xs rounded-md border border-[color:var(--border-default)] bg-surface px-3 py-2 text-[11px] shadow-sm">
      {cluster.topic_label ? (
        <div className="mb-0.5 text-[10px] font-semibold uppercase tracking-wide text-[color:var(--text-secondary)]">
          {cluster.topic_label}
        </div>
      ) : null}
      <div className="text-[12px] font-semibold text-[color:var(--text-primary)]">
        {cluster.label || '(unlabeled)'}
      </div>
      <div className="text-[color:var(--text-muted)]">{cluster.agent_name || '—'}</div>
      <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 tabular-nums text-[color:var(--text-secondary)]">
        <span>{cluster.run_count || 0} runs</span>
        <span>{cluster.error_rate != null ? `${Math.round(cluster.error_rate * 100)}% err` : '—'}</span>
        {cluster.satisfaction_score != null ? (
          <span>sat {Number(cluster.satisfaction_score).toFixed(2)}</span>
        ) : null}
      </div>
    </div>
  );
}

function ConversationHoverCard({ conv }) {
  if (!conv) return null;
  return (
    <div className="pointer-events-none absolute right-3 top-3 max-w-xs rounded-md border border-[color:var(--border-default)] bg-surface px-3 py-2 text-[11px] shadow-sm">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-[color:var(--text-muted)]">Conversation</div>
      <div className="mt-0.5 font-mono text-[11px] text-[color:var(--text-primary)]">
        {(conv.conversation_id || conv.run_id || conv.id || '').slice(0, 24)}
      </div>
      {conv.input_preview ? (
        <div className="mt-1 line-clamp-3 text-[color:var(--text-secondary)]">
          {payloadPreviewText(conv.input_preview)}
        </div>
      ) : null}
      <div className="mt-1 flex gap-3 tabular-nums text-[color:var(--text-muted)]">
        {conv.rage_score != null ? <span>rage {Number(conv.rage_score).toFixed(2)}</span> : null}
        {conv.satisfaction_score != null ? <span>sat {Number(conv.satisfaction_score).toFixed(2)}</span> : null}
      </div>
    </div>
  );
}
